import { forwardRef, useCallback, useEffect, useImperativeHandle, useRef, useState } from "react";
import type { ReactNode } from "react";
import { clamp, cx, formatSec } from "./utils";
import { IconButton } from "./IconButton";
import { Icon } from "./Icon";
import { SegmentedControl } from "./SegmentedControl";

/** Imperative handle exposed via `ref`. */
export interface AudioPlayerHandle {
  play: () => void;
  pause: () => void;
  toggle: () => void;
  /** Seek to an absolute time in seconds. */
  seek: (seconds: number) => void;
  /** Seek relative to the current time (J/K style nudges). */
  nudge: (deltaSeconds: number) => void;
  /** Current time in seconds. */
  currentTime: () => number;
  /** Total duration in seconds (0 until metadata loads). */
  duration: () => number;
  setRate: (rate: number) => void;
}

export type PlaybackRate = 0.5 | 0.75 | 1;

export interface AudioPlayerProps {
  /** Audio URL. */
  src: string;
  /** Fires on every timeupdate — drive a Waveform playhead with this. */
  onTime?: (seconds: number) => void;
  /** Fires once duration is known. */
  onDuration?: (seconds: number) => void;
  onPlay?: () => void;
  onPause?: () => void;
  onEnded?: () => void;
  /** Hide the 0.5/0.75/1 rate control. */
  hideRate?: boolean;
  /** Extra controls rendered at the end of the control row. */
  extra?: ReactNode;
  /** Accessible name. @default "音频播放器" */
  label?: string;
  /** @default "md" */
  size?: "sm" | "md";
  className?: string;
}

const RATES: ReadonlyArray<{ value: PlaybackRate; label: string }> = [
  { value: 0.5, label: "0.5×" },
  { value: 0.75, label: "0.75×" },
  { value: 1, label: "1×" },
];

/**
 * Audio transport: play/pause, scrub bar, elapsed/total time and a
 * 0.5/0.75/1 rate switch. Space toggles playback while the player has focus.
 */
export const AudioPlayer = forwardRef<AudioPlayerHandle, AudioPlayerProps>(function AudioPlayer(
  {
    src,
    onTime,
    onDuration,
    onPlay,
    onPause,
    onEnded,
    hideRate = false,
    extra,
    label = "音频播放器",
    size = "md",
    className,
  },
  ref,
) {
  const audioRef = useRef<HTMLAudioElement>(null);
  const [playing, setPlaying] = useState(false);
  const [time, setTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const [rate, setRate] = useState<PlaybackRate>(1);

  const play = useCallback(() => {
    void audioRef.current?.play?.();
  }, []);
  const pause = useCallback(() => audioRef.current?.pause?.(), []);
  const toggle = useCallback(() => {
    const a = audioRef.current;
    if (!a) return;
    if (a.paused) void a.play?.();
    else a.pause();
  }, []);
  const seek = useCallback((s: number) => {
    const a = audioRef.current;
    if (!a) return;
    a.currentTime = clamp(s, 0, Number.isFinite(a.duration) ? a.duration : s);
    setTime(a.currentTime);
  }, []);

  useImperativeHandle(
    ref,
    (): AudioPlayerHandle => ({
      play,
      pause,
      toggle,
      seek,
      nudge: (d) => seek((audioRef.current?.currentTime ?? 0) + d),
      currentTime: () => audioRef.current?.currentTime ?? 0,
      duration: () =>
        Number.isFinite(audioRef.current?.duration) ? (audioRef.current?.duration ?? 0) : 0,
      setRate: (r) => {
        setRate(r as PlaybackRate);
        if (audioRef.current) audioRef.current.playbackRate = r;
      },
    }),
    [play, pause, toggle, seek],
  );

  useEffect(() => {
    if (audioRef.current) audioRef.current.playbackRate = rate;
  }, [rate, src]);

  return (
    <div
      className={cx("xz-player", `xz-player--${size}`, className)}
      role="group"
      aria-label={label}
      tabIndex={0}
      onKeyDown={(e) => {
        if (e.key === " " || e.key === "Spacebar") {
          e.preventDefault();
          toggle();
        }
      }}
    >
      <audio
        ref={audioRef}
        src={src}
        preload="metadata"
        onPlay={() => {
          setPlaying(true);
          onPlay?.();
        }}
        onPause={() => {
          setPlaying(false);
          onPause?.();
        }}
        onEnded={() => {
          setPlaying(false);
          onEnded?.();
        }}
        onTimeUpdate={(e) => {
          const t = e.currentTarget.currentTime;
          setTime(t);
          onTime?.(t);
        }}
        onLoadedMetadata={(e) => {
          const d = Number.isFinite(e.currentTarget.duration) ? e.currentTarget.duration : 0;
          setDuration(d);
          onDuration?.(d);
        }}
      />
      <IconButton
        icon={<Icon id={playing ? "ui-pause" : "ui-play"} />}
        label={playing ? "暂停" : "播放"}
        variant="primary"
        onClick={toggle}
      />
      <input
        className="xz-player__scrub"
        type="range"
        min={0}
        max={duration || 0}
        step={0.01}
        value={Math.min(time, duration || 0)}
        aria-label="播放进度"
        aria-valuetext={`${formatSec(time)} / ${formatSec(duration)}`}
        onChange={(e) => seek(Number(e.target.value))}
      />
      <span className="xz-player__time" aria-hidden="true">
        {formatSec(time)} / {formatSec(duration)}
      </span>
      {!hideRate && (
        <SegmentedControl<PlaybackRate>
          size="sm"
          label="播放速度"
          options={RATES}
          value={rate}
          onChange={(r) => {
            setRate(r);
            if (audioRef.current) audioRef.current.playbackRate = r;
          }}
        />
      )}
      {extra}
    </div>
  );
});
